/**
 * @fileoverview SEO.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component
 * Manages meta tags for search engine optimization.
 */
function SEO({ title, description, keywords, image, url, schema, noindex = false }) {
  const siteName = "KITH Travels Sri Lanka";
  const defaultDescription = "Visit Sri Lanka with KITH Travels, your premier Sri Lankan DMC. We offer unforgettable vacations in Sri Lanka, featuring bespoke tour plans, authentic cultural experiences, and exclusive Sri Lankan wildlife safaris. Book your dream travel to Sri Lanka today.";
  const defaultKeywords = "Travel Sri Lanka, Visit Sri Lanka, Sri Lankan DMC, Vacation in Sri Lanka, Sri Lankan wildlife, luxury travel Sri Lanka, bespoke tours Sri Lanka, KITH Travels, tour packages, travel agency";
  
  // Format title: "Page Topic | KITH Travels" (under 65 characters)
  let seoTitle = title ? `${title} | ${siteName}` : `Visit Sri Lanka - Premier DMC | ${siteName}`;
  if (seoTitle.length > 65) {
    const allowedLength = 65 - ` | ${siteName}`.length;
    seoTitle = `${title.substring(0, allowedLength - 3)}... | ${siteName}`;
  }
  
  // Format description: include "Sri Lanka", under 155 characters
  let seoDescription = description || defaultDescription;
  if (!seoDescription.toLowerCase().includes("sri lanka")) {
    seoDescription = `${seoDescription} - Sri Lanka`;
  }
  if (seoDescription.length > 155) {
    seoDescription = `${seoDescription.substring(0, 151)}...`;
  }
  
  const seoKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;
  let seoImage = image || "/thumbnail.jpg";
  if (seoImage.startsWith('/')) {
    seoImage = `https://kithtravels.com${seoImage}`;
  }
  let currentPath = window.location.pathname;
  if (currentPath !== '/' && currentPath.endsWith('/')) {
    currentPath = currentPath.slice(0, -1);
  }
  const seoUrl = url || `https://kithtravels.com${currentPath}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <link rel="canonical" href={seoUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />

      {/* Robots */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />

      {/* JSON-LD Schema Markup */}
      {schema && (Array.isArray(schema) ? schema : [schema]).map((sch, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(sch)}
        </script>
      ))}

      {/* Localization Hreflang Tags */}
      <link rel="alternate" hreflang="en" href={`${seoUrl}?lang=en`} />
      <link rel="alternate" hreflang="es" href={`${seoUrl}?lang=es`} />
      <link rel="alternate" hreflang="fr" href={`${seoUrl}?lang=fr`} />
      <link rel="alternate" hreflang="de" href={`${seoUrl}?lang=de`} />
      <link rel="alternate" hreflang="zh" href={`${seoUrl}?lang=zh`} />
      <link rel="alternate" hreflang="it" href={`${seoUrl}?lang=it`} />
      <link rel="alternate" hreflang="ru" href={`${seoUrl}?lang=ru`} />
      <link rel="alternate" hreflang="x-default" href={seoUrl} />
    </Helmet>
  );
}

export default SEO;
